import React, { FC, useState, Fragment } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react/hooks';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { OpterLogRow } from 'models/transport';
import { transportOpterLogTemplate } from 'apollo/templates/transport/query.graphql';

type TransportLogProps = {
    opterOrderId: string | undefined;
};

const TransportLog: FC<TransportLogProps> = ({ opterOrderId }: TransportLogProps) => {
    const { t: translate } = useTranslation(['common', 'transport']);
    const [transportOrderLogs, setTransportLogs] = useState<OpterLogRow[]>([]);
    const [showSpinner, setShowSpinner] = useState<boolean>(true);

    const { error } = useQuery(transportOpterLogTemplate, {
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        variables: { opterOrderId: parseInt(opterOrderId as string) },
        onError: (err) => {
            console.log(err, 'error');
            setShowSpinner(false);
        },
        onCompleted: ({ transportOpterLog }) => {
            setTransportLogs(transportOpterLog);
            setShowSpinner(false);
        },
    });

    return (
        <Fragment>
            {error ? <AlertSnackbar message={translate('transport:ERROR_IN_FETCHING_LOGS')} type="error" /> : undefined}
            <TableContainer>
                <Box mb={1}>
                    <Typography variant="h3">{translate('transport:TRANSPORT_LOG')}</Typography>
                </Box>
                {showSpinner ? (
                    <DelayedLinearProgress />
                ) : !transportOrderLogs.length ? (
                    <Typography variant="subtitle2">{translate('transport:NO_STATUS_LOG_AVAILABLE')}</Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 150 }}>{translate('common:DATE')}</TableCell>
                                <TableCell>{translate('transport:EVENT')}</TableCell>
                                <TableCell>{translate('transport:MESSAGE_FROM_CARRIER')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transportOrderLogs.map((transportOrderLog) => (
                                <TableRow key={transportOrderLog.DateAndTime || transportOrderLog.DateTimeString}>
                                    <TableCell>
                                        {transportOrderLog.DateAndTime || transportOrderLog.DateTimeString}
                                    </TableCell>
                                    <TableCell>
                                        <Box>{transportOrderLog.Status}</Box>
                                        {transportOrderLog.hasImage ? (
                                            <Box>{translate('transport:ATTACHED_IMAGE_FROM_DRIVER')}</Box>
                                        ) : undefined}
                                    </TableCell>
                                    <TableCell>
                                        <Box>{transportOrderLog.POD_Descr}</Box>
                                        {transportOrderLog.hasImage ? (
                                            <Box>
                                                <Link
                                                    target="_blank"
                                                    href={`https://denthub.driftcentral.net/opter/Report/TTShowAttachment?OFF_Id=${transportOrderLog.OFF_Id}&Id=${transportOrderLog.DEA_Guid}`}
                                                >
                                                    {translate('transport:SHOW_ATTACHED_IMAGE')}
                                                </Link>
                                            </Box>
                                        ) : undefined}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>
        </Fragment>
    );
};

export default TransportLog;
